import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { ArrowForwardIcon, DoneIcon } from 'icons/materialIcons';
import useProgressTracker from 'utils/dashboard/useProgressTracker';
import { useAuth } from 'contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Gradient border styles
const createGradientBorderStyles = (borderRadius = '12px') => ({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: borderRadius,
    border: '1px solid transparent',
    background: 'linear-gradient(to bottom right, #36dad6, #685ed1) border-box',
    WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'destination-out',
    mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    maskComposite: 'exclude',
    pointerEvents: 'none'
  }
});

const ProgressBox = ({ title, description, completed, onClick }) => {
  const isClickable = !completed;

  return (
    <Box
      onClick={isClickable ? onClick : undefined}
      sx={{
        width: '100%',
        backgroundColor: completed ? '#f1f7fa' : 'white',
        border: '1px solid',
        borderColor: completed ? '#dee3e6' : '#ededed',
        borderRadius: '8px',
        padding: '16px',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        position: 'relative',
        height: '120px',
        minHeight: '120px',
        maxHeight: '120px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...(!completed && {
          '&:hover': {
            ...createGradientBorderStyles('8px')
          }
        })
      }}
    >
      {completed ? (
        <>
          <Box
            sx={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              backgroundColor: '#29aef8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
            }}
          >
            <DoneIcon sx={{ color: 'white', fontSize: '12px' }} />
          </Box>
          <Typography
            variant="h6"
            align="center"
            sx={{
              color: '#212121',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            {title}
          </Typography>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#212121',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px'
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#554f4f',
                fontSize: '0.85rem',
                fontWeight: 500,
                lineHeight: '1.6'
              }}
            >
              {description}
            </Typography>
          </Box>
          <ArrowForwardIcon sx={{ color: '#212121', marginLeft: '12px' }} />
        </Box>
      )}
    </Box>
  );
};

const ProgressUI = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);

  const [isCheckingProgress, setIsCheckingProgress] = useState(false);
  const lastCheckTimeRef = useRef(0);
  const DEBOUNCE_DELAY = 2000;

  const {
    createCard,
    addTwoLinks,
    addProfilePhoto,
    shareCard,
    captureLead,
    checkCardShare,
    checkCardCreation,
    checkProfilePhoto,
    checkTwoLinks,
    checkLeadCapture,
    updateCompletedSteps,
    completedSteps,
  } = useProgressTracker();

  const { authState } = useAuth();
  const userId = authState.user?.userId;

  const isSignupFlowActive = localStorage.getItem('signupInProgress') === 'true';
  const isAuthenticated = authState?.isAuthenticated;

  useEffect(() => {
    const runProgressChecks = async () => {
      const now = Date.now();

      if (isCheckingProgress) {
        return;
      }

      if (now - lastCheckTimeRef.current < DEBOUNCE_DELAY) {
        return;
      }

      const currentSignupFlowActive = localStorage.getItem('signupInProgress') === 'true';
      const currentIsAuthenticated = authState?.isAuthenticated;

      if (!userId || !currentIsAuthenticated || currentSignupFlowActive) {
        return;
      }

      setIsCheckingProgress(true);
      lastCheckTimeRef.current = now;

      try {
        const progressPromises = [
          checkCardCreation(userId).catch(error => {
            return false;
          }),
          checkProfilePhoto(userId).catch(error => {
            return false;
          }),
          checkTwoLinks(userId).catch(error => {
            return false;
          }),
          checkLeadCapture(userId).catch(error => {
            return false;
          }),
          checkCardShare(userId).catch(error => {
            return false;
          })
        ];

        await Promise.all(progressPromises);

      } catch (error) {
      } finally {
        setIsCheckingProgress(false);
      }
    };

    if (userId && isAuthenticated && !isSignupFlowActive && !isCheckingProgress) {
      runProgressChecks();
    }

  }, [
    userId,
    isAuthenticated,
    isSignupFlowActive,
    checkCardCreation,
    checkProfilePhoto,
    checkTwoLinks,
    checkLeadCapture,
    checkCardShare,
    authState?.isAuthenticated
  ]);

  const isStep2Complete = addTwoLinks && addProfilePhoto;
  const allStepsCompleted = createCard && isStep2Complete && shareCard && captureLead;

  const progressStepStyle = (completed) => ({
    width: '110px',
    height: '8px',
    backgroundColor: completed ? '#29aef8' : '#e0e0e0',
    border: `1px solid ${completed ? '#2499da' : '#c5c5c5'}`,
    borderRadius: '5px',
  });

  const progressSteps = [
    {
      title: 'Create your card',
      description: 'Add a card to your profile',
      completed: createCard,
      onClick: () => navigate('/dashboard/cards/new')
    },
    {
      title: 'Setup your card',
      description: 'Add at least two links to your card and a profile picture',
      completed: isStep2Complete,
      onClick: () => navigate('/dashboard/cards/edit')
    },
    {
      title: 'Share your card',
      description: 'Share your card',
      completed: shareCard,
      onClick: () => navigate('/dashboard/share')
    },
    {
      title: 'Capture a lead',
      description: 'Use the lead capture form to capture a lead',
      completed: captureLead,
      onClick: () => navigate('/dashboard/leads')
    }
  ];

  const isOnSignupPath = window.location.pathname.includes('/sign-up') ||
    window.location.pathname.includes('/signup') ||
    window.location.pathname.includes('/sign_up');

  if (isSignupFlowActive || !isAuthenticated || isOnSignupPath) {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#f5fcff',
        borderRadius: '12px',
        padding: allStepsCompleted ? '16px' : '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: allStepsCompleted ? '0px' : '24px',
        margin: '16px auto',
        marginBottom: allStepsCompleted ? '40px' : '16px',
        ...createGradientBorderStyles('8px')
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
        }}
      >
        <Box sx={{ flex: 1, marginBottom: { xs: '16px', sm: 0 } }}>
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '16px' }}>
            {allStepsCompleted ? 'Congrats, you\'ve completed all steps!' : 'Get Started with Touch'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.85rem',
              fontWeight: 500,
              color: '#4F4F4F',
              lineHeight: '1.6',
            }}
          >
            {allStepsCompleted
              ? 'Your Touch team is now set up for success.'
              : 'Complete these steps to make your Touch experience better'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box sx={progressStepStyle(createCard)} />
          <Box sx={progressStepStyle(isStep2Complete)} />
          <Box sx={progressStepStyle(shareCard)} />
          <Box sx={progressStepStyle(captureLead)} />
          <Typography variant="body2" sx={{ color: '#b0b0b0', marginLeft: '8px', marginRight: allStepsCompleted ? '16px' : '0' }}>
            {completedSteps}/4 Completed
          </Typography>

          {allStepsCompleted && (
            <Button
              variant="contained"
              onClick={() => setIsVisible(false)}
              sx={{
                backgroundColor: '#29aef8',
                borderRadius: '20px',
                color: 'white',
                textTransform: 'none',
                padding: '4px 16px',
                height: '32px',
                fontSize: '0.85rem',
                '&:hover': {
                  backgroundColor: '#1e88e5',
                },
              }}
            >
              Dismiss
            </Button>
          )}
        </Box>
      </Box>

      {!allStepsCompleted && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: 'repeat(4, 1fr)'
            },
            gap: '16px',
            width: '100%',
            height: 'auto',
            maxHeight: '200px',
            overflow: 'hidden'
          }}
        >
          {progressSteps.map((step, index) => (
            <ProgressBox
              key={index}
              title={step.title}
              description={step.description}
              completed={step.completed}
              onClick={step.onClick}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProgressUI;